import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useInvoices } from "../../../lib/invoices/useInvoices";
import { useOrg } from "../../../lib/org/useOrg";
import { Card, EmptyState, formatCents } from "./shared";

// Returns the last 12 calendar months as {key: "YYYY-MM", label: "MMM yy"}.
// The newest month is last (rightmost on the chart).
function last12Months(now: Date = new Date()) {
  const out: Array<{ key: string; label: string }> = [];
  const anchor = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 11; i >= 0; i--) {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const label = d.toLocaleString("en-US", {
      month: "short",
      year: "2-digit",
    });
    out.push({ key: `${y}-${m}`, label });
  }
  return out;
}

export function MonthlyTrend({ submittedBy }: { submittedBy?: string | null }) {
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { activeRole } = useOrg();

  // Employees don't see company-wide revenue — the revenue line is cross-org
  // data. Fall back to the single-line (expenses only) rendering they had
  // before.
  const includeRevenue = activeRole !== "employee";

  const data = useMemo(() => {
    const months = last12Months();
    const expenseTotals = new Map<string, number>(
      months.map((m) => [m.key, 0]),
    );
    const revenueTotals = new Map<string, number>(
      months.map((m) => [m.key, 0]),
    );
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      const key = e.occurredOn.slice(0, 7);
      if (expenseTotals.has(key)) {
        expenseTotals.set(key, (expenseTotals.get(key) ?? 0) + e.amountCents);
      }
    }
    if (includeRevenue) {
      for (const inv of invoices) {
        if (inv.direction !== "outgoing") continue;
        const key = inv.issuedOn.slice(0, 7);
        if (revenueTotals.has(key)) {
          revenueTotals.set(
            key,
            (revenueTotals.get(key) ?? 0) + inv.amountCents,
          );
        }
      }
    }
    return months.map((m) => ({
      month: m.label,
      expense: expenseTotals.get(m.key) ?? 0,
      revenue: revenueTotals.get(m.key) ?? 0,
    }));
  }, [expenses, invoices, submittedBy, includeRevenue]);

  const hasExpense = data.some((d) => d.expense > 0);
  const hasRevenue = data.some((d) => d.revenue > 0);
  const hasData = includeRevenue ? hasExpense || hasRevenue : hasExpense;

  const emptyMessage = includeRevenue
    ? "No revenue or expenses in the last 12 months yet."
    : "No spending in the last 12 months yet.";

  return (
    <Card title="Monthly trend (last 12 months)" testId="report-monthly-trend">
      {!hasData ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <>
          {/* Hidden markers — recharts does not forward arbitrary
              `data-testid` props onto the rendered SVG path reliably, so we
              expose line identity via siblings the e2e spec can assert on. */}
          <span data-testid="monthly-trend-expense-line" className="sr-only">
            expense line
          </span>
          {includeRevenue && (
            <span data-testid="monthly-trend-revenue-line" className="sr-only">
              revenue line
            </span>
          )}
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatCents(Number(v))}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  width={80}
                />
                <Tooltip formatter={(v: number) => formatCents(v)} />
                {includeRevenue && (
                  <Legend
                    verticalAlign="top"
                    height={24}
                    wrapperStyle={{ fontSize: "11px" }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="expense"
                  name="Expenses"
                  stroke="#0070F2"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#0070F2" }}
                  isAnimationActive={false}
                />
                {includeRevenue && (
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#10B981" }}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}
